import {
  Gauge, TrendingDown, TrendingUp, AlertTriangle, ShieldAlert,
  CheckCircle, Activity, Target, Users, Key
} from "lucide-react";
import { getRelativePercentage } from "../../../lib/utils";
import { CloudEntityQuickLinks } from "./CloudEntityQuickLinks";
import { EntitySignalBar } from "./EntitySignalBar";

interface RiskScoreData {
  cloudProvider: string;
  score: number;
  level: string;
  breakdown: { category: string; score: number; weight: number }[];
  trend: string;
  delta: number;
  riskFactors?: any[];
  recommendations?: any[];
  metrics?: Record<string, any>;
  severityBreakdown?: Record<string, number>;
  topRiskyNhis?: any[];
  topRiskySecrets?: any[];
  recentAlerts?: any[];
}

interface ExpandedRiskScoreCardProps {
  data: RiskScoreData;
}

export const ExpandedRiskScoreCard = ({ data }: ExpandedRiskScoreCardProps) => {
  const { cloudProvider, score, level, breakdown, delta, riskFactors = [], recommendations = [], topRiskyNhis = [], topRiskySecrets = [], recentAlerts = [], severityBreakdown = {} } = data;
  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (score / 100) * circumference;
  const maxRiskFactorCount = Math.max(...riskFactors.map((factor) => Number(factor?.count ?? 0)), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-xl bg-sky-500/10">
          <Gauge className="w-4 h-4 text-sky-500" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h3 className="font-semibold text-[15px] text-foreground">Overall Risk Score Dashboard</h3>
            {delta !== null && delta !== undefined && (
              <div className={`flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[11px] font-medium ${delta < 0 ? "bg-success/10 text-success" : "bg-primary/10 text-primary"}`}>
                {delta < 0 ? <TrendingDown className="w-2.5 h-2.5" /> : <TrendingUp className="w-2.5 h-2.5" />}
                {Math.abs(delta)}% from last month
              </div>
            )}
          </div>
          <p className="text-[13px] text-muted-foreground">Comprehensive security risk assessment leveraging cloud anomaly mapping</p>
        </div>
      </div>

      {/* Main Score Section */}
      <div className="grid grid-cols-3 gap-6">
        {/* Score Gauge */}
        <div className="flex flex-col items-center justify-center">
          <div className="relative w-28 h-28">
            <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="45" fill="none" stroke="hsl(var(--muted))" strokeWidth="5" />
              <circle
                cx="50" cy="50" r="45" fill="none"
                stroke={score > 70 ? "hsl(var(--primary))" : score > 40 ? "hsl(var(--warning, 38 92% 50%))" : "hsl(var(--success, 142 71% 45%))"}
                strokeWidth="5"
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={strokeDashoffset}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-3xl font-bold text-foreground">{score}</span>
              <span className="text-[13px] text-muted-foreground">{level}</span>
            </div>
          </div>

          {/* Category Breakdown */}
          <div className="w-full mt-4 space-y-2">
            {breakdown.map((item) => (
               <div key={item.category} className="flex items-center gap-2">
                 <span className="text-[11px] text-muted-foreground w-14">{item.category}</span>
                 <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                   <div className="h-full rounded-full bg-sky-500" style={{ width: `${item.score}%` }} />
                 </div>
                 <span className="text-[12px] font-bold w-6 text-right">{Math.round(item.score)}%</span>
               </div>
            ))}
          </div>
        </div>

        {/* Risk Factors mapped using weight */}
        <div className="col-span-2">
          <div className="flex items-center justify-between mb-3">
             <h3 className="text-[14px] font-semibold flex items-center gap-2">
               <AlertTriangle className="w-4 h-4 text-sky-500" /> Key Risk Drivers
             </h3>
             <span className="text-[11px] text-muted-foreground">Weighted influence on overall score</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {riskFactors.map((factor) => (
              <div key={factor.name} className="p-3 rounded-lg bg-card border border-border/40 hover:bg-muted/10 transition-colors">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="font-semibold text-[13px] text-foreground truncate">{factor.name}</span>
                  <span className="text-[12px] font-bold text-sky-500">{factor.count}</span>
                </div>
                <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-1">
                   <span>Relative volume</span>
                   <span>{factor.weight}% weight</span>
                </div>
                <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-primary/40 rounded-full" style={{ width: `${getRelativePercentage(factor.count, maxRiskFactorCount)}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="border-t border-border/10 pt-6">
         <h4 className="text-[14px] font-bold text-foreground mb-4">Highest Risk Entity Exposure</h4>
         <div className="grid grid-cols-2 gap-6">
            <div className="space-y-3">
               <div className="flex items-center gap-2 text-[12px] font-bold text-muted-foreground uppercase tracking-wider">
                 <Users className="w-3.5 h-3.5 text-sky-500" /> Top Risky NHIs
               </div>
               <div className="space-y-1.5 flex flex-col">
                  {topRiskyNhis.length > 0 ? topRiskyNhis.slice(0, 3).map((nhi: any, idx: number) => (
                    <div key={idx} className="flex flex-col gap-1.5 text-[13px] p-2.5 rounded-lg bg-muted/10 border border-border/5 hover:bg-muted/30 transition-colors">
                        <div className="flex justify-between items-center w-full">
                          <div className="flex items-center gap-2 flex-1 min-w-0 pr-2">
                            <div className="w-1.5 h-1.5 rounded-full mt-0.5 shrink-0 bg-primary" />
                            <span className="font-semibold text-foreground truncate" title={nhi.name}>{nhi.name}</span>
                          </div>
                        </div>
                        <CloudEntityQuickLinks
                          cloudProvider={cloudProvider}
                          entityKind="nhi"
                          entity={nhi}
                        />
                        <div className="space-y-2 mt-1.5 w-full">
                          <EntitySignalBar
                            icon={ShieldAlert}
                            label="Alerts"
                            percentage={nhi.alertPct}
                            accentClassName="text-primary/80"
                            trackClassName="bg-primary/10"
                            fillClassName="bg-primary"
                          />
                          <EntitySignalBar
                            icon={Activity}
                            label="Activity"
                            percentage={nhi.activityPct}
                            accentClassName="text-sky-500/80"
                            trackClassName="bg-sky-500/10"
                            fillClassName="bg-sky-500"
                          />
                        </div>
                    </div>
                  )) : (
                     <div className="p-4 text-center text-muted-foreground text-[12px] bg-muted/5 rounded-lg border border-border/5">No high risk NHIs</div>
                  )}
               </div>
            </div>

            <div className="space-y-3">
               <div className="flex items-center gap-2 text-[12px] font-bold text-muted-foreground uppercase tracking-wider">
                 <Key className="w-3.5 h-3.5 text-sky-500" /> Top Risky Secrets
               </div>
               <div className="space-y-1.5 flex flex-col">
                  {topRiskySecrets.length > 0 ? topRiskySecrets.slice(0, 3).map((secret: any, idx: number) => (
                    <div key={idx} className="flex flex-col gap-1.5 text-[13px] p-2.5 rounded-lg bg-muted/10 border border-border/5 hover:bg-muted/30 transition-colors">
                        <div className="flex justify-between items-center w-full">
                          <div className="flex items-center gap-2 flex-1 min-w-0 pr-2">
                            <div className="w-1.5 h-1.5 rounded-full mt-0.5 shrink-0 bg-primary" />
                            <span className="font-semibold text-foreground truncate" title={secret.name || secret.secretName}>{secret.name || secret.secretName}</span>
                          </div>
                        </div>
                        <CloudEntityQuickLinks
                          cloudProvider={cloudProvider}
                          entityKind="secret"
                          entity={secret}
                        />
                        <div className="space-y-2 mt-1.5 w-full">
                          <EntitySignalBar
                            icon={ShieldAlert}
                            label="Alerts"
                            percentage={secret.alertPct}
                            accentClassName="text-primary/80"
                            trackClassName="bg-primary/10"
                            fillClassName="bg-primary"
                          />
                          <EntitySignalBar
                            icon={Activity}
                            label="Activity"
                            percentage={secret.activityPct}
                            accentClassName="text-sky-500/80"
                            trackClassName="bg-sky-500/10"
                            fillClassName="bg-sky-500"
                          />
                        </div>
                    </div>
                  )) : (
                     <div className="p-4 text-center text-muted-foreground text-[12px] bg-muted/5 rounded-lg border border-border/5">No high risk Secrets</div>
                  )}
               </div>
            </div>
         </div>
      </div>

      <div className="grid grid-cols-3 gap-6 pt-6 border-t border-border/10">
        <div className="col-span-2 space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-[13px] font-bold text-foreground">Cloud Anomaly Alerts Timeline</h4>
            <div className="flex items-center gap-1 text-[10px] font-bold bg-muted/10 px-2 py-0.5 rounded border border-border/10 text-muted-foreground">
               CRIT: <span className="text-primary">{severityBreakdown.critical || 0}</span>
               <span className="mx-1 text-border">|</span>
               HIGH: <span className="text-primary/70">{severityBreakdown.high || 0}</span>
               <span className="mx-1 text-border">|</span>
               MED: <span className="text-sky-500/70">{severityBreakdown.medium || 0}</span>
            </div>
          </div>
          
          <div className="rounded-xl border border-border/50 bg-card overflow-hidden">
            {recentAlerts.length > 0 ? (
              <div className="divide-y divide-border/10 max-h-[300px] overflow-y-auto custom-scrollbar">
                {recentAlerts.map((alert, idx) => {
                  const isPrimary = alert.severity?.toLowerCase() === 'critical' || alert.severity?.toLowerCase() === 'high';
                  return (
                    <div key={idx} className="p-3 text-[12px] flex items-start gap-3">
                       <ShieldAlert className={`w-4 h-4 shrink-0 mt-0.5 ${isPrimary ? 'text-primary' : 'text-sky-500'}`} />
                       <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-semibold text-foreground leading-tight">{alert.title}</span>
                            <span className="text-[10px] text-muted-foreground shrink-0 pl-3">
                              {alert.createdAt ? new Date(alert.createdAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'}) : ''}
                            </span>
                          </div>
                          <p className="text-[11px] text-muted-foreground truncate" title={alert.entity}>{alert.entity}</p>
                       </div>
                    </div>
                  );
                })}
              </div>
            ) : (
               <div className="p-6 text-center flex flex-col items-center gap-2">
                 <ShieldAlert className="w-6 h-6 text-muted-foreground/30" />
                 <span className="text-muted-foreground text-[12px]">No recent alerts</span>
               </div>
            )}
          </div>
        </div>

        {/* Recommendations */}
        <div className="space-y-3">
          <h3 className="text-[13px] font-bold text-foreground flex items-center gap-2">
            <Target className="w-4 h-4 text-sky-500" />
            Recommended Actions
          </h3>
          <div className="space-y-2">
            {recommendations.length > 0 ? recommendations.map((rec: any, idx: number) => (
              <div key={idx} className="p-3 rounded-lg bg-card border border-border/40 hover:bg-muted/10 transition-all">
                <div className="flex items-start gap-2">
                  <div className={`p-1 mt-0.5 rounded ${rec.priority?.toLowerCase() === "critical" ? "bg-primary/10 text-primary" : "bg-sky-500/10 text-sky-500"}`}>
                    <CheckCircle className="w-3 h-3" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-[12px] leading-snug">{rec.action}</p>
                    <div className="flex items-center justify-between mt-1">
                      <span className={`text-[10px] uppercase font-bold tracking-wider ${rec.priority?.toLowerCase() === "critical" ? "text-primary" : "text-sky-500"}`}>{rec.priority}</span>
                      <span className="text-[10px] text-foreground font-bold bg-muted/50 px-1.5 py-0.5 rounded">{rec.count} items</span>
                    </div>
                  </div>
                </div>
              </div>
            )) : (
              <div className="p-4 text-center text-[12px] text-muted-foreground border border-border/30 border-dashed rounded-xl">No pending recommendations</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
